/** Collision radius of a root mission (world units). Hubs keep room for their nest ring. */
export function nodeRadius(i, size, childCount, podR) {
  if (childCount[i]) return 0.68 + size[i] * 0.2 + podR[i] * 0.52;
  return 0.42 + size[i] * 0.18;
}

/** Tight ring that seats n satellites evenly around a hub. */
export function podRadius(n, depth = 1) {
  const base = depth >= 2 ? 0.55 : 0.72;
  const gap = 0.82 * base;
  return Math.max(0.95 * base, (n * gap) / (2 * Math.PI)) + 0.32 * base;
}

/**
 * Compact organic layout of one island, written into x/y at the given root indexes.
 * Local coordinates, centred on the origin when done.
 */
export function layoutIsland({ rs, x, y, rad, edges, rnd }) {
  const n = rs.length;
  if (n === 0) return 1;
  if (n === 1) { x[rs[0]] = 0; y[rs[0]] = 0; return rad.get(rs[0]) + 1.2; }

  // Golden-angle disk: already a pretty seed, the sim just evens it out.
  const spread = Math.sqrt(n) * 1.55;
  rs.forEach((i, k) => {
    const a = k * 2.399963 + rnd() * 0.4;
    const d = Math.sqrt((k + 0.5) / n) * spread;
    x[i] = Math.cos(a) * d;
    y[i] = Math.sin(a) * d;
  });

  const vx = new Map(); const vy = new Map();
  rs.forEach((i) => { vx.set(i, 0); vy.set(i, 0); });
  const inIsland = new Set(rs);
  const local = edges.filter(([a, b]) => inIsland.has(a) && inIsland.has(b));

  const ITER = Math.min(360, 140 + n * 8);
  for (let iter = 0; iter < ITER; iter += 1) {
    const cool = 0.85 * (1 - iter / ITER) + 0.15;
    rs.forEach((i) => { vx.set(i, vx.get(i) * 0.62); vy.set(i, vy.get(i) * 0.62); });

    // All-pairs: keep a breathing gap, fade past ~3 diameters so the blob stays round.
    for (let a = 0; a < n; a += 1) {
      const i = rs[a];
      const ri = rad.get(i);
      for (let b = a + 1; b < n; b += 1) {
        const j = rs[b];
        let dx = x[i] - x[j]; let dy = y[i] - y[j];
        let dist = Math.hypot(dx, dy);
        if (dist < 1e-4) { dx = (rnd() - 0.5); dy = (rnd() - 0.5); dist = Math.hypot(dx, dy) || 1e-4; }
        const minD = ri + rad.get(j) + 0.72;
        if (dist > minD * 3.4) continue;
        const overlap = minD - dist;
        const f = overlap > 0 ? overlap * 0.7 : (minD * minD) / (dist * dist) * 0.1;
        const ux = dx / dist; const uy = dy / dist;
        vx.set(i, vx.get(i) + ux * f); vy.set(i, vy.get(i) + uy * f);
        vx.set(j, vx.get(j) - ux * f); vy.set(j, vy.get(j) - uy * f);
      }
    }

    // Wires: short rest, so connected work sits next to each other without stretching the island.
    local.forEach(([a, b]) => {
      const dx = x[b] - x[a]; const dy = y[b] - y[a];
      const dist = Math.hypot(dx, dy) || 1;
      const rest = rad.get(a) + rad.get(b) + 0.7;
      const f = (dist - rest) * 0.14;
      const ux = dx / dist; const uy = dy / dist;
      vx.set(a, vx.get(a) + ux * f); vy.set(a, vy.get(a) + uy * f);
      vx.set(b, vx.get(b) - ux * f); vy.set(b, vy.get(b) - uy * f);
    });

    // Soft pull to the running centroid — keeps the island a blob, not a comet.
    let cx = 0; let cy = 0;
    rs.forEach((i) => { cx += x[i]; cy += y[i]; });
    cx /= n; cy /= n;
    rs.forEach((i) => {
      vx.set(i, vx.get(i) + (cx - x[i]) * 0.035);
      vy.set(i, vy.get(i) + (cy - y[i]) * 0.035);
      x[i] += vx.get(i) * cool;
      y[i] += vy.get(i) * cool;
    });
  }

  // Hard resolve leftover overlaps, then centre.
  for (let pass = 0; pass < 28; pass += 1) {
    let moved = false;
    for (let a = 0; a < n; a += 1) {
      const i = rs[a];
      for (let b = a + 1; b < n; b += 1) {
        const j = rs[b];
        let dx = x[i] - x[j]; let dy = y[i] - y[j];
        let dist = Math.hypot(dx, dy);
        const need = rad.get(i) + rad.get(j) + 0.58;
        if (dist < 1e-4) { dx = 1; dy = 0; dist = 1; }
        if (dist >= need) continue;
        moved = true;
        const push = (need - dist) * 0.5;
        const ux = dx / dist; const uy = dy / dist;
        x[i] += ux * push; y[i] += uy * push;
        x[j] -= ux * push; y[j] -= uy * push;
      }
    }
    if (!moved) break;
  }

  let cx = 0; let cy = 0;
  rs.forEach((i) => { cx += x[i]; cy += y[i]; });
  cx /= n; cy /= n;
  let r = 1.4;
  rs.forEach((i) => {
    x[i] -= cx; y[i] -= cy;
    r = Math.max(r, Math.hypot(x[i], y[i]) + rad.get(i));
  });
  return r + 0.22;
}
